import { MdBarChart } from 'react-icons/md';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AgentApiAnalysisInsightsStageBreakdownGroup } from '@/types/serializers';
import InsightEmptyState from './InsightEmptyState';
import {
  LEAD_TIME_BAR_STAGES_GROUPED,
  LEAD_TIME_BAR_STAGES_UNGROUPED,
} from './insightStageDefinitions';

// Recharts は SVG 属性として色を受け取るためインラインで定義する。値はデザインシステム
// セマンティックトークン Semantic/Text/Sub・Semantic/Text/White と一致する。
const AXIS_TICK_COLOR = '#6D6D6D';
const BAR_LABEL_COLOR = '#FFFFFF';
// 目盛りの破線グリッド色。Semantic/Border と一致する。
const GRID_COLOR = '#D1D1D1';

// 担当者ラベル領域の幅。Figma の `gap-[7px]` を含めて十分な長さの氏名（[未割当]アカウントなど）
// が表示できる幅を確保する。凡例の左パディングと同期させる。
const Y_AXIS_WIDTH = 120;
const Y_AXIS_TICK_MARGIN = 7;

// バー内ラベルのフォントサイズ（px）。入り切るかどうかの幅判定にも使う。
const BAR_LABEL_FONT_SIZE = 16;
// ラベル左右の最低限の余白（px）。これ未満の幅しかなければラベルを隠してホバー表示に切り替える。
const BAR_LABEL_PADDING = 8;

// Recharts の TooltipProps / LabelList props は内部実装に依存して型が緩いため、
// content prop へ渡すコンポーネント用に最小限のローカル型を定義する。
type ChartTooltipPayloadItem = {
  dataKey?: string | number;
  value?: number | string;
  color?: string;
  name?: string | number;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: ChartTooltipPayloadItem[];
};

type DaysLabelProps = {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: unknown;
};

type BarSegmentProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  payload?: { _lastPositiveStage?: string };
};

/**
 * 積み上げバーの各セグメントの描画。値(>0)を持つ最後のステージ（=バー右端）のみ
 * 右側を角丸にする。左端は角丸にしない（Figma レビュー指摘）。
 * 後続ステージが 0 日でも実際に値を持つ右端セグメントが角丸になるよう、
 * index ではなく各行の値で右端を判定する。
 */
function createBarSegmentShape(stageKey: string) {
  return function BarSegment(props: BarSegmentProps) {
    if ((Number(props.width) || 0) <= 0) return null;
    const isRightEnd = props.payload?._lastPositiveStage === stageKey;
    return <Rectangle {...props} radius={isRightEnd ? [0, 4, 4, 0] : [0, 0, 0, 0]} />;
  };
}

interface Props {
  /** API から取得したグループ別ステージ内訳（グループなし時は1要素） */
  groups: AgentApiAnalysisInsightsStageBreakdownGroup[];
  /** グループ化されているか（true なら担当者ラベル列を表示する） */
  isGrouped: boolean;
}

/** バー内に表示する日数ラベル（X日） */
function formatDaysLabel(value: unknown): string {
  if (typeof value !== 'number' || value <= 0) return '';
  return `${value}日`;
}

/**
 * ラベル文字列の描画幅をおおまかに見積もる（16px相当）。
 * 「日」は全角、「.」は細く、その他（数字）は中間幅として扱う。
 */
function estimateLabelWidth(text: string): number {
  return [...text].reduce((acc, ch) => {
    if (ch === '日') return acc + BAR_LABEL_FONT_SIZE;
    if (ch === '.') return acc + 4;
    return acc + 9;
  }, 0);
}

/**
 * バーセグメント内の日数ラベル。セグメント幅に収まる場合のみ描画し、
 * 収まらない場合は非表示にする（値はホバーTooltipで表示する）。
 */
function renderDaysLabel({ x, y, width, height, value }: DaysLabelProps) {
  const text = formatDaysLabel(value);
  if (!text) return null;
  const segWidth = Number(width) || 0;
  if (segWidth < estimateLabelWidth(text) + BAR_LABEL_PADDING) return null;
  return (
    <text
      x={Number(x) + segWidth / 2}
      y={Number(y) + Number(height) / 2}
      textAnchor="middle"
      dominantBaseline="central"
      fill={BAR_LABEL_COLOR}
      fontSize={BAR_LABEL_FONT_SIZE}
      fontWeight={700}
    >
      {text}
    </text>
  );
}

/**
 * ホバー時のTooltip（Figma 3318:29300 準拠）。
 * 積み上げ Bar では shared={false} が発火しないため shared（行全体）で受け、
 * ホバーした行（担当者）の各ステージの日数を表示する。
 * これにより、バー内に入り切らずラベルを隠したセグメントの値も確認できる。
 */
function LeadTimeTooltip({ active, payload }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  const items = payload.filter((p) => (Number(p.value) || 0) > 0);
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-xxs rounded-sm bg-white p-xs text-body-xs text-main shadow-layer2">
      {items.map((item) => (
        <div key={String(item.dataKey)} className="flex items-center gap-xxs">
          <span
            className="inline-block size-4 shrink-0 rounded-xs"
            style={{ backgroundColor: item.color }}
            aria-hidden
          />
          <span>{item.name}</span>
          <span className="font-bold">{formatDaysLabel(item.value)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * 担当者氏名が長すぎてY軸領域からはみ出すケースを救う。
 * 9文字を超える場合は末尾を「…」で省略する。
 */
function formatGroupName(name: string): string {
  return name.length > 9 ? `${name.slice(0, 9)}…` : name;
}

/**
 * X軸の domain と ticks を動的に算出する。
 * - 最低でも Figma と同じ 0〜35 のスケールを確保しつつ、データがそれを超える場合は
 *   キリの良い刻みで max を切り上げる。
 * - fixedStep を指定した場合はその刻みで固定する（グループ表示は 20 刻み）。
 *   未指定時はデータ量に応じて 5/10 刻みを自動選択する（非グループ表示）。
 */
function calculateXAxisConfig(
  rows: Array<Record<string, number | string>>,
  stageKeys: string[],
  fixedStep?: number
): { domain: [number, number]; ticks: number[] } {
  const sums = rows.map((row) => stageKeys.reduce((acc, key) => acc + (Number(row[key]) || 0), 0));
  const dataMax = sums.length ? Math.max(...sums) : 0;
  const rawMax = Math.max(35, dataMax);
  const step = fixedStep ?? (rawMax <= 35 ? 5 : 10);
  const max = Math.ceil(rawMax / step) * step;
  const ticks = Array.from({ length: max / step + 1 }, (_, i) => i * step);
  return { domain: [0, max], ticks };
}

/**
 * ステージごとの平均リードタイムを横方向の積み上げバーで表示する。
 * API レスポンスの stages: [{ key, days }] を recharts 用にフラット化する。
 */
export default function InsightLeadTimeBar({ groups, isGrouped }: Props) {
  // グループ表示・非グループ表示ともに登録を除く6ステージ（Figma配色）
  const stages = isGrouped ? LEAD_TIME_BAR_STAGES_GROUPED : LEAD_TIME_BAR_STAGES_UNGROUPED;

  const chartData = groups.map((row) => {
    const base: Record<string, number | string> = { groupName: row.group_name };
    row.stages.forEach((s) => {
      base[s.key] = s.days;
    });
    // 値(>0)を持つ最後のステージ（積み上げ描画順）= バー右端のセグメント。
    // shape 側で右端だけ角丸にするために保持する。
    const lastPositive = [...stages].reverse().find((st) => (Number(base[st.key]) || 0) > 0);
    if (lastPositive) base._lastPositiveStage = lastPositive.key;
    return base;
  });

  // groups が空、もしくは全ステージの合計日数が 0 ならデータなしとみなす
  const stageKeys = stages.map((s) => s.key);
  const hasData = chartData.some((row) => stageKeys.some((key) => (Number(row[key]) || 0) > 0));
  if (!hasData) {
    return (
      <InsightEmptyState
        icon={MdBarChart}
        description="条件を変更すると、ここにグラフが表示されます"
      />
    );
  }

  const barSize = isGrouped ? 35 : 148;
  const chartHeight = isGrouped ? Math.max(groups.length, 1) * 51 + 60 : 246;

  // グループ表示は目盛りを 20 刻みに固定する（Figma レビュー指摘）。非グループは自動刻み。
  const { domain: xAxisDomain, ticks: xAxisTicks } = calculateXAxisConfig(
    chartData,
    stages.map((s) => s.key),
    isGrouped ? 20 : undefined
  );

  return (
    <div className="w-full [&_.recharts-surface:focus:not(:focus-visible)]:outline-hidden [&_.recharts-wrapper_*:focus:not(:focus-visible)]:outline-hidden [&_.recharts-wrapper:focus:not(:focus-visible)]:outline-hidden">
      {/* Figma: 凡例はバー開始位置（YAxis 右側）に揃える。grouped 時は YAxis 幅ぶん左に詰める。 */}
      <div
        className="mb-md flex flex-wrap items-center gap-x-xs gap-y-xxs"
        style={isGrouped ? { paddingLeft: Y_AXIS_WIDTH + Y_AXIS_TICK_MARGIN } : undefined}
      >
        {stages.map((s) => (
          <div key={s.key} className="flex items-center gap-xs">
            <span
              className="inline-block size-4 shrink-0 rounded-xs"
              style={{ backgroundColor: s.color }}
              aria-hidden
            />
            <span className="text-body-xs text-main">{s.label}</span>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={chartData}
          // グループなし時は YAxis 幅が 0 のため、X軸の「0」目盛りが左端で見切れる。
          // 左マージンを確保して「0」を表示する（グループ表示は YAxis 幅で余白を確保済み）。
          margin={{ top: 8, right: 24, left: isGrouped ? 0 : 8, bottom: 24 }}
          barCategoryGap={isGrouped ? 16 : 0}
        >
          {/* 目盛りに合わせた縦の破線グリッド（Figma準拠）。横線は表示しない。 */}
          <CartesianGrid horizontal={false} strokeDasharray="4 4" stroke={GRID_COLOR} />
          <XAxis
            type="number"
            domain={xAxisDomain}
            ticks={xAxisTicks}
            tick={{ fontSize: 12, fill: AXIS_TICK_COLOR }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="groupName"
            width={isGrouped ? Y_AXIS_WIDTH : 0}
            tick={isGrouped ? { fontSize: 12, fill: AXIS_TICK_COLOR } : false}
            tickFormatter={isGrouped ? formatGroupName : undefined}
            tickMargin={isGrouped ? Y_AXIS_TICK_MARGIN : 0}
            axisLine={false}
            tickLine={false}
          />
          {/* ホバー行の各ステージ日数をまとめて表示。入り切らずに隠したラベルをここで補う。 */}
          <Tooltip cursor={false} content={<LeadTimeTooltip />} />
          {stages.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId="lead-time"
              fill={s.color}
              barSize={barSize}
              isAnimationActive={false}
              shape={createBarSegmentShape(s.key)}
            >
              {/* 入り切る場合のみバー内に日数を描画。入り切らない場合はホバーTooltipで表示する。 */}
              <LabelList dataKey={s.key} content={renderDaysLabel} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
